import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Badge, Button, Drawer, Space, Avatar, Dropdown } from 'antd';
import { HeartOutlined, HomeOutlined, LoginOutlined, MenuOutlined, PhoneOutlined, ShopOutlined, ShoppingCartOutlined, UserOutlined } from '@ant-design/icons';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { fetchComboPacks } from '../services/productService';
import '../styles/Navbar.css';

const Navbar: React.FC = () => {
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { customer, isAuthenticated, logout } = useCustomerAuth();

  // Hide the "Weekly Packs" entry until at least one pack exists
  const [hasPacks, setHasPacks] = useState(false);
  // Hide the "Veggie Advisor" entry when the admin disables it on web
  const [advisorOn, setAdvisorOn] = useState(false);
  // Health Packs appears only once a pack is actually configured and open —
  // the section is kept out of the shop entirely, so an empty menu entry would
  // be the only thing pointing at an empty page.
  const [hasHealthPacks, setHasHealthPacks] = useState(false);
  useEffect(() => {
    fetchComboPacks().then(packs => setHasPacks(packs.length > 0)).catch(() => {});
    const API = process.env.REACT_APP_API_URL || '';
    fetch(`${API}/api/advisor/config`).then(r => r.json())
      .then(j => setAdvisorOn(!!j.web_enabled)).catch(() => {});
    // Health Packs is its own section, kept out of the shop, so the menu is
    // the main way anyone finds it. Shown only once a pack is actually open —
    // an entry pointing at an empty page is worse than no entry.
    fetch(`${API}/api/products/preorder/packs`).then(r => r.json())
      .then(j => setHasHealthPacks(Array.isArray(j.data) && j.data.length > 0))
      .catch(() => {});
  }, []);

  const navLinks = [
    { label: 'Home', path: '/', icon: <HomeOutlined /> },
    { label: 'Shop Vegetables', path: '/shop', icon: <ShopOutlined /> },
    ...(hasHealthPacks ? [{ label: 'Health Packs', path: '/health-packs', icon: <HeartOutlined /> }] : []),
    ...(advisorOn ? [{ label: 'Veggie Advisor', path: '/advisor', icon: <ShopOutlined /> }] : []),
    ...(hasPacks ? [{ label: 'Weekly Packs', path: '/shop?tab=packs', icon: <ShoppingCartOutlined /> }] : []),
    { label: 'How It Works', path: '/#how-it-works', icon: <PhoneOutlined /> },
  ];

  // Navigate, but for in-page hash links (e.g. /#how-it-works) scroll directly when
  // we're already on that page — otherwise navigate and let the page handle the scroll.
  const go = (path: string) => {
    setDrawerOpen(false);
    const hashIdx = path.indexOf('#');
    if (hashIdx >= 0) {
      const base = path.slice(0, hashIdx) || '/';
      if (window.location.pathname === base) {
        document.getElementById(path.slice(hashIdx + 1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        navigate(path, { replace: true }); // reflect the section in the URL so the menu highlights
        return;
      }
    }
    // Home: when already on the home page, scroll back to the top and clear any
    // section hash so "Home" (not "How It Works") shows as the active menu item.
    if (path === '/' && window.location.pathname === '/') {
      if (window.location.hash) navigate('/', { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(path);
  };

  // Which nav link matches the current location (for highlighting the active menu).
  const isActive = (path: string): boolean => {
    const hashIdx = path.indexOf('#');
    if (hashIdx >= 0) {
      const base = path.slice(0, hashIdx) || '/';
      return location.pathname === base && location.hash === path.slice(hashIdx);
    }
    const [purePath, query] = path.split('?');
    if (location.pathname !== purePath) return false;
    if (purePath === '/shop') {
      // Shop Vegetables vs Weekly Packs (/shop?tab=packs) share the /shop route.
      const current = new URLSearchParams(location.search).get('tab') === 'packs';
      const link = query ? new URLSearchParams(query).get('tab') === 'packs' : false;
      return current === link;
    }
    // Home is active only when not viewing the How-It-Works section.
    if (purePath === '/') return location.hash !== '#how-it-works';
    return true;
  };

  const accountName =
    [customer?.first_name, customer?.last_name].filter(Boolean).join(' ') ||
    customer?.name || 'My Account';
  const accountInitial =
    customer?.first_name?.[0]?.toUpperCase() ||
    customer?.name?.[0]?.toUpperCase() ||
    customer?.mobile?.slice(-2) || '?';

  const menuItems: any[] = [
    { key: 'dashboard', label: '📦 My Orders', onClick: () => navigate('/dashboard?tab=orders') },
    { key: 'addresses', label: '📍 My Addresses', onClick: () => navigate('/dashboard?tab=addresses') },
    { key: 'wishlist', label: '❤️ Wishlist', onClick: () => navigate('/dashboard?tab=wishlist') },
    { key: 'coins', label: '🪙 F2k Coins', onClick: () => navigate('/dashboard?tab=coins') },
    { key: 'profile', label: '👤 Profile', onClick: () => navigate('/dashboard?tab=profile') },
    { type: 'divider' },
    {
      key: 'logout',
      label: '🚪 Logout',
      danger: true,
      onClick: () => { logout(); navigate('/login'); },
    },
  ];

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <Link to="/" className="navbar-logo">
          <img src="/delhi36-logo.png" alt="Delhi36 F2K" className="navbar-logo-img" />
          <span className="navbar-logo-text-wrap">
            <span className="navbar-logo-title">Delhi36 <span className="navbar-logo-f2k">F2K</span></span>
            <span className="navbar-logo-sub">Farm to Kitchen</span>
          </span>
        </Link>

        <Space size={4} className="desktop-nav">
          {navLinks.map(link => (
            <Button
              key={link.path} type="text" onClick={() => go(link.path)}
              className={`navbar-nav-btn${isActive(link.path) ? ' navbar-nav-btn--active' : ''}`}
            >
              {link.label}
            </Button>
          ))}
        </Space>

        <Space size={8}>
          <Badge count={count} color="#FF9800">
            <Button
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate('/cart')}
              type="text"
              className="navbar-cart-btn"
            />
          </Badge>

          {isAuthenticated ? (
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={['click']}
              popupRender={menu => (
                <div className="navbar-account-card">
                  <div className="navbar-account-head">
                    <Avatar size={42} src={customer?.avatar_url} className="navbar-account-avatar">
                      {!customer?.avatar_url && accountInitial}
                    </Avatar>
                    <div className="navbar-account-head-info">
                      <span className="navbar-account-name">{accountName}</span>
                      {customer?.mobile && <span className="navbar-account-phone">+91 {customer.mobile}</span>}
                    </div>
                  </div>
                  {menu}
                </div>
              )}
            >
              <Avatar
                className="navbar-user-avatar"
                size={36}
                src={customer?.avatar_url}
              >
                {!customer?.avatar_url && accountInitial}
              </Avatar>
            </Dropdown>
          ) : (
            <Button icon={<UserOutlined />} onClick={() => navigate('/login')} className="navbar-login-btn">
              <span className="hide-mobile">Login</span>
            </Button>
          )}

          <Button
            icon={<MenuOutlined />}
            type="text"
            className="mobile-menu-btn"
            onClick={() => setDrawerOpen(true)}
          />
        </Space>
      </div>

      <Drawer
        title={
          <span className="navbar-logo">
            <img src="/delhi36-logo.png" alt="Delhi36 F2K" className="navbar-logo-img" />
            <span className="navbar-logo-text">Delhi36 <span>F2K</span></span>
          </span>
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="left"
        size="default"
      >
        <Space orientation="vertical" size={8} className="navbar-drawer-space">
          {navLinks.map(link => (
            <Button
              key={link.path} block type="text" icon={link.icon}
              onClick={() => go(link.path)}
              className={`navbar-drawer-item${isActive(link.path) ? ' navbar-drawer-item--active' : ''}`}
            >
              {link.label}
            </Button>
          ))}
          {isAuthenticated ? (
            <>
              <Button
                block
                onClick={() => { navigate('/dashboard'); setDrawerOpen(false); }}
                className="navbar-drawer-login"
              >
                📦 My Dashboard
              </Button>
              <Button
                block danger
                onClick={() => { logout(); navigate('/login'); setDrawerOpen(false); }}
              >
                🚪 Logout
              </Button>
            </>
          ) : (
            <Button
              block icon={<LoginOutlined />}
              onClick={() => { navigate('/login'); setDrawerOpen(false); }}
              className="navbar-drawer-login"
            >
              Login / Register
            </Button>
          )}
          <Button block className="green-btn" onClick={() => { navigate('/admin'); setDrawerOpen(false); }}>
            Admin Panel
          </Button>
        </Space>
      </Drawer>
    </nav>
  );
};

export default Navbar;
